import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { PLAN_PRICES_INR, PROJECT_PLANS } from '@mys/contracts/constants';
import type { ProjectPlan } from '@mys/contracts';
import { ApiError } from '../lib/api.js';
import { useClient } from '../lib/clients.js';
import { useCreateProject } from '../lib/projects.js';
import { formatInr } from '../lib/format.js';

/* ============================================================================
   /projects/new?clientId=... — start the work.
   ----------------------------------------------------------------------------
   Always reached from a client, because a project without one cannot exist.

   Picking a plan fills in its list price, but the price is still sent as its
   own field: discounts and part-payments are real, and a price silently
   derived from a plan is a discount nobody can find later.
   ========================================================================== */

export function NewProjectPage() {
  const [params] = useSearchParams();
  const clientId = params.get('clientId') ?? '';
  const navigate = useNavigate();

  const client = useClient(clientId);
  const create = useCreateProject();

  const [plan, setPlan] = useState<ProjectPlan>('business');
  const [priceInr, setPriceInr] = useState(String(PLAN_PRICES_INR.business));
  const [promisedDate, setPromisedDate] = useState('');
  /* Once someone edits the price, choosing a plan stops overwriting it. A form
     that resets a negotiated figure because you changed your mind about the
     label is a form people stop trusting. */
  const [priceTouched, setPriceTouched] = useState(false);

  function choosePlan(value: ProjectPlan) {
    setPlan(value);
    if (!priceTouched) setPriceInr(String(PLAN_PRICES_INR[value]));
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate(
      {
        clientId,
        plan,
        priceInr: Number(priceInr),
        ...(promisedDate ? { promisedDate } : {}),
      },
      { onSuccess: (data) => void navigate(`/projects/${data.project.id}`, { replace: true }) },
    );
  }

  const fieldError = (name: string): string | undefined =>
    create.error instanceof ApiError ? create.error.fields[name] : undefined;

  const formError =
    create.error instanceof ApiError && Object.keys(create.error.fields).length === 0
      ? create.error.message
      : null;

  if (!clientId) {
    return (
      <>
        <p className="alert" role="alert">
          It is not clear which client this project is for. Start from the client page.
        </p>
        <Link to="/clients" className="btn">
          Open clients
        </Link>
      </>
    );
  }

  if (client.isPending) return <p className="muted">Loading…</p>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>New project</h1>
          <p className="muted">{client.data?.client.business ?? 'Client'}</p>
        </div>
      </div>

      <form className="card stack" onSubmit={onSubmit} noValidate>
        <fieldset className="field" style={{ border: 0, margin: 0, padding: 0 }}>
          <legend style={{ padding: 0, marginBottom: 6 }}>Plan</legend>
          <div className="row">
            {PROJECT_PLANS.map((value) => (
              <button
                key={value}
                type="button"
                className={plan === value ? 'btn btn--small' : 'btn btn--ghost btn--small'}
                aria-pressed={plan === value}
                onClick={() => choosePlan(value)}
              >
                {value} · {formatInr(PLAN_PRICES_INR[value])}
              </button>
            ))}
          </div>
          {fieldError('plan') ? (
            <span className="field__error" role="alert">
              {fieldError('plan')}
            </span>
          ) : null}
        </fieldset>

        <div className="field">
          <label htmlFor="priceInr">Price (₹)</label>
          <input
            id="priceInr"
            type="number"
            inputMode="numeric"
            min={0}
            value={priceInr}
            onChange={(event) => {
              setPriceTouched(true);
              setPriceInr(event.target.value);
            }}
          />
          <span className="muted" style={{ fontSize: 13 }}>
            Change it here if you have given a discount.
          </span>
          {fieldError('priceInr') ? (
            <span className="field__error" role="alert">
              {fieldError('priceInr')}
            </span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="promisedDate">Promised date</label>
          <input
            id="promisedDate"
            type="date"
            value={promisedDate}
            onChange={(event) => setPromisedDate(event.target.value)}
          />
          <span className="muted" style={{ fontSize: 13 }}>
            The date promised to the client. /refund is measured against it — you can leave it
            blank.
          </span>
          {fieldError('promisedDate') ? (
            <span className="field__error" role="alert">
              {fieldError('promisedDate')}
            </span>
          ) : null}
        </div>

        {formError ? (
          <p className="alert" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="row">
          <button className="btn" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create project'}
          </button>
          <Link to={`/clients/${clientId}`} className="btn btn--ghost">
            Cancel
          </Link>
        </div>
      </form>
    </>
  );
}
